import { Box, Divider, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import { ActionLink, CurrencyFormatters, Icon, IconName } from "halifax";
import { capitalize } from "lodash";
import React, { useContext, useMemo } from "react";
import {
  BookedRooms,
  HotelItinerary,
  MyTripsFilter,
  PaymentLineItemEnum,
  PaymentLineItemTravelWallet,
  PaymentLineItemTravelWalletEnum,
  PaymentTypeEnum,
} from "redmond";

import { getHotelStayDuration } from "../../../../../../utils/constants";
import { NIGHT } from "../HotelPaymentModalContent/constants";

import * as textConstants from "./constants";
import "./styles.scss";
import { ClientContext } from "../../../../../../../../App";
import { usePrintItemizedReceipt } from "../../usePrintItemizedReceipt";
import {
  ActiveExperiments,
  useExperiment,
} from "../../../../../../../../context/experiments";

interface IHotelItemizedReceiptModalContentProps {
  hotel: HotelItinerary;
  documentId?: string;
  isMobile?: boolean;
  tripsFilter: MyTripsFilter;
}

export const HotelItemizedReceiptModalContent = ({
  hotel,
  documentId = textConstants.DEFAULT_DOC_ID,
  isMobile,
  tripsFilter,
}: IHotelItemizedReceiptModalContentProps) => {
  const { reservation, ancillaries, numCompNights } = hotel;
  const { paymentAmountInfo, paymentBreakdown } = hotel;
  const breakdownPayments =
    paymentBreakdown?.payments as PaymentLineItemTravelWallet[];
  const walletItemPayments = breakdownPayments.filter(
    (breakdownPayment) =>
      breakdownPayment.PaymentLineItem === PaymentLineItemEnum.TravelWallet
  );
  const {
    pricing,
    guests,
    lodgingData,
    reservationId,
    createdAt,
    checkInDate,
    checkOutDate,
    bookedRooms,
  } = reservation;
  const { cfar } = ancillaries;
  const { rooms, taxes, tripTotal, feeBreakdown } = pricing;
  const { logo } = useContext(ClientContext);

  const bookedRoomsCount = bookedRooms.reduce(
    (acc: number, bR: BookedRooms) => acc + bR.count,
    0
  );
  const stayLength = dayjs(checkOutDate).diff(checkInDate, "day");
  const basePrice = rooms.fiat.value;
  const fees = feeBreakdown?.total?.value;
  const feesCurrency = feeBreakdown?.total?.currencySymbol;
  const totalTaxes = taxes.fiat.value;

  // CFAR
  const cfarCharge = cfar?.premium.amount.fiat.value;
  const isNegativePremiumCfar = cfarCharge != null ? cfarCharge < 0 : false;
  const shouldShowCfarCharge = cfarCharge != null && !isNegativePremiumCfar;

  // This value will be used to calculate the effective base price.
  // To match other "discount" values, we use the absolute value of the negative premium.
  const negativePremiumDiscountOrZero = Math.abs(Math.min(cfarCharge ?? 0, 0));

  const effectiveBasePrice = basePrice - negativePremiumDiscountOrZero;

  const isNthNightPromoEnabled = useExperiment(
    ActiveExperiments.NTH_NIGHT_PROMO_ITEMIZED_RECEIPT
  );

  const paidNights = stayLength - (numCompNights ?? 0);
  const hasCompNights =
    isNthNightPromoEnabled && !!numCompNights && paidNights > 0;
  // Comp'd nights carry no cost, so the accommodation/taxes totals are spread
  // across only the paid nights rather than the full length of stay.
  const nightDivisor = hasCompNights ? paidNights : stayLength;

  const effectiveBasePricePerNightRemainderInCents =
    (effectiveBasePrice * 100) % nightDivisor;

  const effectiveBasePricePerNightInCents =
    (effectiveBasePrice * 100 - effectiveBasePricePerNightRemainderInCents) /
    nightDivisor;

  const totalTaxesPerNightRemainderInCents =
    (totalTaxes * 100) % nightDivisor;

  const totalTaxesPerNightPricePerNightInCents =
    (totalTaxes * 100 - totalTaxesPerNightRemainderInCents) / nightDivisor;

  const total = tripTotal.fiat.value;
  const effectiveTotal =
    total -
    walletItemPayments.reduce((prev, curr) => prev + curr.amount.amount, 0);

  const invoiceDate = dayjs(createdAt, "YYYY-MM-DDTHH:mm:ss.SSS").format(
    textConstants.MMDDYYYY_FORMAT
  );
  const guestName = `${guests[0].givenName} ${guests[0].surname}`;
  const resId = `H-${reservationId}`;
  const addCurrency = (num: number) =>
    `${CurrencyFormatters.get(rooms.fiat.currencyCode).format(Math.abs(num))}`;

  const { printing, handlePrint } = usePrintItemizedReceipt({
    documentId,
    tripId: reservationId,
    travelType: "hotel",
    tripsFilter,
    printWindowTitle: `${guestName} - ${resId} ${textConstants.TITLE}`,
  });

  const paymentInfo = {
    numberDisplay: "",
    rewardsDisplay: "",
    creditApplied: breakdownPayments.some(
      (pmt) =>
        pmt.TravelWallet === PaymentLineItemTravelWalletEnum.TravelWalletCredit
    ),
    offerApplied: breakdownPayments.some(
      (pmt) =>
        pmt.TravelWallet === PaymentLineItemTravelWalletEnum.TravelWalletOffer
    ),
  };

  if (paymentAmountInfo?.PaymentAmountInfo === PaymentTypeEnum.FiatAmountInfo) {
    paymentInfo.numberDisplay = paymentAmountInfo.numberDisplay;
  } else if (
    paymentAmountInfo?.PaymentAmountInfo === PaymentTypeEnum.RewardsAmountInfo
  ) {
    paymentInfo.numberDisplay = paymentAmountInfo.accountDisplayName;
  } else if (
    paymentAmountInfo?.PaymentAmountInfo === PaymentTypeEnum.SplitAmountInfo
  ) {
    paymentInfo.numberDisplay = paymentAmountInfo.fiatInfo.numberDisplay;
    paymentInfo.rewardsDisplay =
      paymentAmountInfo.rewardsInfo.accountDisplayName;
  }

  const renderPrintButton = useMemo(() => {
    if (printing) return null;

    if (isMobile) return null;

    return (
      <ActionLink
        className="print-btn"
        content={textConstants.PRINT}
        onClick={handlePrint}
      />
    );
  }, [isMobile, printing]);

  return (
    <Box className="itemized-receipt-modal-container" id={documentId}>
      <Box className="itemized-receipt-modal-content">
        <Box className="itemized-receipt-logo-container">
          {logo}
          {renderPrintButton}
        </Box>
        <Box className="itemized-receipt-title-container">
          <Typography variant="h2">{textConstants.TITLE}</Typography>
        </Box>
        <Box className="itemized-receipt-details">
          <Box className="line-item">
            <Typography variant="body1">{textConstants.HOTEL_NAME}:</Typography>
            <Typography variant="body1" className="hotel-name bold">
              {lodgingData.name}
            </Typography>
          </Box>
          <Box className="line-item">
            <Typography variant="body1">{textConstants.GUEST_NAME}:</Typography>
            <Typography variant="body1" className="bold">
              {guestName}
            </Typography>
          </Box>
          <Box className="line-item gutter-bottom">
            <Typography variant="body1">
              {textConstants.CONFIRMATION_NUMBER}:
            </Typography>
            <Typography variant="body1" className="bold">
              {resId}
            </Typography>
          </Box>
          <Box className="line-item">
            <Typography variant="body1">
              {textConstants.INVOICE_DATE}:
            </Typography>
            <Typography variant="body1" className="bold">
              {invoiceDate}
            </Typography>
          </Box>
          <Box className="line-item">
            <Typography variant="body1">{textConstants.CHECK_IN}:</Typography>
            <Typography variant="body1" className="bold">
              {dayjs(checkInDate).format(textConstants.MMDDYYYY_FORMAT)}
            </Typography>
          </Box>
          <Box className="line-item">
            <Typography variant="body1">{textConstants.CHECKOUT}:</Typography>
            <Typography variant="body1" className="bold">
              {dayjs(checkOutDate).format(textConstants.MMDDYYYY_FORMAT)}
            </Typography>
          </Box>
          <Box className="line-item gutter-bottom">
            <Typography variant="body1">{textConstants.YOUR_STAY}:</Typography>
            <Typography variant="body1" className="bold">
              {`${getHotelStayDuration(reservation)}, ${bookedRoomsCount} ${
                bookedRoomsCount === 1
                  ? textConstants.ROOM
                  : textConstants.ROOMS
              }`}
            </Typography>
          </Box>
          <Divider className="line-item gutter-bottom" />
          <Typography variant="body1" className="gutter-bottom bold">
            {textConstants.TOTAL_COST_PER_NIGHT}
          </Typography>
          {new Array(stayLength).fill(true).map((_, i) => {
            const dateStr = dayjs(checkInDate)
              .add(i, "day")
              .format(textConstants.MMDDYYYY_FORMAT);

            const isCompedNight = hasCompNights && i >= paidNights;

            const priceRoundUpIndex = Math.floor(
              nightDivisor - effectiveBasePricePerNightRemainderInCents
            ); // index of first 'round-up' bin

            const taxRoundUpIndex = Math.floor(
              nightDivisor - totalTaxesPerNightRemainderInCents
            );

            const accommodationCents = isCompedNight
              ? effectiveBasePricePerNightInCents
              : i >= priceRoundUpIndex
              ? effectiveBasePricePerNightInCents + 1
              : effectiveBasePricePerNightInCents;

            const taxCents = isCompedNight
              ? totalTaxesPerNightPricePerNightInCents
              : i >= taxRoundUpIndex
              ? totalTaxesPerNightPricePerNightInCents + 1
              : totalTaxesPerNightPricePerNightInCents;

            const nightTotal = isCompedNight
              ? 0
              : (accommodationCents + taxCents) / 100;

            return (
              <Box key={dateStr}>
                <Typography variant="body1">{`${dateStr} (${capitalize(
                  NIGHT
                )} ${i + 1})`}</Typography>
                <Box className="line-item">
                  <Typography variant="body1">
                    {textConstants.ACCOMMODATION}
                  </Typography>
                  <Typography variant="body1">
                    {isCompedNight ? (
                      <>
                        <span className="strikethrough">
                          {addCurrency(accommodationCents / 100)}
                        </span>{" "}
                        {addCurrency(0)}
                      </>
                    ) : (
                      addCurrency(accommodationCents / 100)
                    )}
                  </Typography>
                </Box>
                <Box className="line-item">
                  <Typography variant="body1">
                    {textConstants.TAXES_AND_FEES}
                  </Typography>
                  <Typography variant="body1">
                    {isCompedNight ? (
                      <>
                        <span className="strikethrough">
                          {addCurrency(taxCents / 100)}
                        </span>{" "}
                        {addCurrency(0)}
                      </>
                    ) : (
                      addCurrency(taxCents / 100)
                    )}
                  </Typography>
                </Box>
                <Box className="line-item gutter-bottom">
                  <Typography variant="body1" className="bold">
                    {textConstants.getNightTotalLabel(i + 1)}
                  </Typography>
                  <Typography variant="body1" className="bold">
                    {addCurrency(nightTotal)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
          {hasCompNights && (
            <Box className="complimentary-night-pill-container">
              <Box className="complimentary-night-pill">
                <Icon name={IconName.Sparkle} />
                <Typography variant="body2">
                  <span className="bold">
                    {textConstants.COMPLIMENTARY_NIGHT_PILL_PREFIX}
                  </span>{" "}
                  {textConstants.getComplimentaryNightPillText(
                    numCompNights!
                  )}
                </Typography>
              </Box>
            </Box>
          )}
          <Box className="line-item">
            <Typography variant="body1" className="bold">
              {textConstants.ACCOMMODATION_TOTAL}
            </Typography>
            <Typography variant="body1" className="bold">
              {addCurrency(effectiveBasePrice)}
            </Typography>
          </Box>
          <Box className="line-item">
            <Typography variant="body1" className="bold">
              {textConstants.TAXES_AND_FEES_TOTAL}
            </Typography>
            <Typography variant="body1" className="bold">
              {addCurrency(totalTaxes)}
            </Typography>
          </Box>
          {shouldShowCfarCharge && (
            <Box className="line-item">
              <Typography variant="body1" className="bold">
                {textConstants.CANCEL_FOR_ANY_REASON}
              </Typography>
              <Typography variant="body1" className="bold">
                {addCurrency(cfarCharge)}
              </Typography>
            </Box>
          )}
          {!!fees && (
            <Box className="line-item">
              <Typography variant="body1" className="bold">
                {textConstants.FEES}
              </Typography>
              <Typography variant="body1" className="bold">
                {`${feesCurrency}${fees}`}
              </Typography>
            </Box>
          )}
          {walletItemPayments.map((breakdownPayment) =>
            "breakdown" in breakdownPayment &&
            breakdownPayment.breakdown?.length ? (
              breakdownPayment.breakdown.map((breakdownItem) => (
                <Box className="line-item positive">
                  <Typography variant="body1" className="bold">
                    {breakdownItem.description ||
                      textConstants.TRAVEL_CREDITS_LINE_ITEM_APPLIED}
                  </Typography>
                  <Typography variant="body1" className="bold">
                    -{addCurrency(Math.abs(breakdownItem.amount.amount))}
                  </Typography>
                </Box>
              ))
            ) : (
              <Box className="line-item positive">
                <Typography variant="body1" className="bold">
                  {breakdownPayment.TravelWallet ===
                  PaymentLineItemTravelWalletEnum.TravelWalletCredit
                    ? textConstants.TRAVEL_CREDITS_LINE_ITEM_APPLIED
                    : textConstants.TRAVEL_OFFER_LINE_ITEM_APPLIED}
                </Typography>
                <Typography variant="body1" className="bold">
                  -{addCurrency(Math.abs(breakdownPayment.amount.amount))}
                </Typography>
              </Box>
            )
          )}
          <Box className="line-item gutter-bottom">
            <Typography variant="body1" className="bold">
              {textConstants.TOTAL_COST}
            </Typography>
            <Typography variant="body1" className="bold">
              {addCurrency(effectiveTotal)}
            </Typography>
          </Box>
          <Typography variant="caption" className="line-item gutter-bottom">
            {textConstants.COPY1}
          </Typography>
          {((paymentInfo.numberDisplay &&
            !paymentInfo.numberDisplay?.includes("Unavailable")) ||
            paymentInfo.rewardsDisplay ||
            paymentInfo.creditApplied ||
            paymentInfo.offerApplied) && (
            <Box>
              <Typography variant="body1" className="gutter-bottom bold">
                {textConstants.PAYMENT_INFO}
              </Typography>

              {paymentInfo.numberDisplay && (
                <Typography variant="body1" className="gutter-bottom">
                  {`...${paymentInfo.numberDisplay.slice(-4)}`}
                </Typography>
              )}

              {paymentInfo.rewardsDisplay && (
                <Typography variant="body1" className="gutter-bottom">
                  {`...${paymentInfo.rewardsDisplay.slice(-4)}`}
                </Typography>
              )}

              {paymentInfo.creditApplied &&
                !paymentInfo.rewardsDisplay &&
                !paymentInfo.numberDisplay && (
                  <Typography variant="body1" className="gutter-bottom">
                    {textConstants.TRAVEL_CREDIT_APPLIED}
                  </Typography>
                )}

              {paymentInfo.offerApplied &&
                !paymentInfo.rewardsDisplay &&
                !paymentInfo.numberDisplay && (
                  <Typography variant="body1" className="gutter-bottom">
                    {textConstants.TRAVEL_OFFER_APPLIED}
                  </Typography>
                )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
